// Cart: review portions from several groups, see the server's totals, then pay once for everything.
import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translateKnown } from '../i18n/knownMessages';
import { formatDeadline } from '../utils/formatDeadline';
import { getCurrentUser } from '../utils/auth';
import { stateLabel } from '../utils/usStates';

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
  return stripePromise;
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const PROCESSING_MESSAGE = 'Your payment is processing. Please do not pay again; your order will appear shortly.';
const UNCONFIRMED_MESSAGE = 'We could not confirm your payment status. Check status before paying again.';

function PayForm({ clientSecret, onPaid, onRecheck, onCancel, cancelling }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Set when the payment status is unresolved (processing, unknown, or not payable): Pay stays disabled so it is not charged twice
  const [uncertain, setUncertain] = useState(false);
  // A ref, not state: a second submit in the same tick must not start a second confirmation
  const inFlight = useRef(false);

  const hold = (message) => { setUncertain(true); setError(message); };

  const pay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || inFlight.current || uncertain) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (paymentIntent?.status === 'succeeded') return onPaid();
      if (paymentIntent?.status === 'processing') return hold(PROCESSING_MESSAGE);
      if (!stripeError) return hold(UNCONFIRMED_MESSAGE);
      if (stripeError.code === 'payment_intent_unexpected_state') {
        // Already paid (here or in another browser): never confirm again, show the paid state
        const status = stripeError.payment_intent?.status;
        if (status === 'succeeded') return onPaid();
        if (status === 'processing') return hold(PROCESSING_MESSAGE);
        // Canceled or not payable: stop here and point to the existing checkout recovery
        return hold('This checkout can no longer be paid. Use Cancel checkout, then start checkout again.');
      }
      if (['card_error', 'validation_error'].includes(stripeError.type)) {
        // Declined, insufficient funds or invalid details: Stripe's own message, and the form can be corrected and retried
        return setError(stripeError.message || 'Your card was not accepted. Check the details and try again.');
      }
      // Network or unknown errors: the payment may have gone through, so do not allow a retry until the status is rechecked
      return hold(UNCONFIRMED_MESSAGE);
    } catch (err) {
      return hold(UNCONFIRMED_MESSAGE);
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  return (
    <form onSubmit={pay} className="space-y-3">
      <div className="border p-3 rounded"><CardElement /></div>
      {error && <p className="text-red-600">{translateKnown(t, error)}</p>}
      {uncertain && onRecheck && <button type="button" onClick={onRecheck} className="text-blue-600 hover:underline">{t('cart.checkStatus')}</button>}
      {uncertain && <Link to="/my-orders" className="text-blue-600 hover:underline">{t('orders.title')}</Link>}
      <button type="submit" disabled={busy || uncertain} className="bg-green-600 text-white px-4 py-2 rounded w-full disabled:opacity-50">
        {busy ? t('cart.processing') : t('cart.payNow')}
      </button>
      {/* Cancelling is blocked while a payment is in flight or its status is unresolved, so cancel never races a charge */}
      {onCancel && (
        <button type="button" onClick={onCancel} disabled={busy || uncertain || cancelling} className="border border-gray-400 text-gray-700 px-4 py-2 rounded w-full transition active:scale-[0.98] active:brightness-90 disabled:opacity-50">
          {cancelling ? t('cart.cancelling', { defaultValue: 'Cancelling...' }) : t('cart.cancelCheckout')}
        </button>
      )}
    </form>
  );
}

// The server answers an empty cart with this message instead of an empty quote
const EMPTY_CART = 'Your cart is empty';

// The last quote per user, shown at once when the cart reopens while the server re-prices it in the background.
// Checkout stays disabled until a fresh quote has arrived, so a cached price can never be paid.
const quoteCache = new Map();

export default function CartPage() {
  const { t } = useTranslation();
  const userId = getCurrentUser()?.userId || null;
  const cached = userId ? quoteCache.get(userId) : undefined;
  const [quote, setQuote] = useState(cached || null);
  const [verified, setVerified] = useState(false); // true once the current quote came from the server
  const [message, setMessage] = useState('');
  const [payment, setPayment] = useState(null); // { clientSecret, total }
  const [active, setActive] = useState(null); // { checkoutId, total, clientSecret } of an unpaid checkout
  const [done, setDone] = useState(false);
  const [method, setMethod] = useState('card'); // 'card' or 'cash', chosen before checkout
  const [cashOrder, setCashOrder] = useState(null); // { deadline } once a cash order is placed
  const [submitting, setSubmitting] = useState(false); // blocks a second Checkout or Pay with Cash click while one is in flight
  const [busyCheckout, setBusyCheckout] = useState(null); // 'resume' or 'cancel' while the unpaid checkout buttons are working
  const [rowError, setRowError] = useState(null); // { itemId, text } for the row whose change was rejected
  // True until the first quote request settles, so the drawer shows a loading line instead of a blank panel
  const [loading, setLoading] = useState(!cached);
  // Payment succeeded: the server empties the paid items when it creates the orders, which can be a
  // moment later, so the header is refreshed again after a short delay
  const paid = () => {
    const purchased = (quote?.lines || []).reduce((n, l) => n + l.quantity, 0);
    setDone(true);
    // Header drops the purchased count at once, then re-reads the server once the webhook has processed the order
    window.dispatchEvent(new CustomEvent('cart-paid', { detail: { count: purchased } }));
    [2000, 6000].forEach((ms) => setTimeout(() => window.dispatchEvent(new Event('cart-changed')), ms));
  };

  // Each load has a number; only the newest answer is applied, so an older response cannot overwrite a newer one
  const latestLoad = useRef(0);
  const load = async () => {
    const run = ++latestLoad.current;
    const isLatest = () => run === latestLoad.current;
    try {
      // An empty cart is read from the cart itself and shown without pricing it; if that read fails, the quote decides
      let items = null;
      try {
        items = (await api.get('/api/cart')).data?.items;
      } catch (readErr) {
        items = null;
      }
      if (!isLatest()) return;
      if (Array.isArray(items) && items.length === 0) {
        quoteCache.delete(userId);
        setQuote(null);
        setVerified(false);
        setMessage(EMPTY_CART);
        return;
      }
      const res = await api.post('/api/cart/quote');
      if (!isLatest()) return;
      if (userId) quoteCache.set(userId, res.data);
      setQuote(res.data);
      setVerified(true);
      setMessage('');
    } catch (err) {
      if (!isLatest()) return;
      const text = err.response?.data?.message || 'Could not load your cart.';
      // An empty cart must not keep showing the items it had; any other failure keeps the last items visible, unverified
      if (text === EMPTY_CART) quoteCache.delete(userId);
      setVerified(false);
      setQuote(text === EMPTY_CART ? null : quote);
      setMessage(text);
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  // Loads once on open; later changes call load() directly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Rows whose change is being validated. Only that row waits: a second click on it would send a stale quantity.
  const [pending, setPending] = useState(() => new Set());
  const inFlight = useRef(new Set()); // checked before state updates, so two clicks in one tick cannot both start
  const change = async (line, quantity) => {
    if (inFlight.current.has(line.itemId)) return;
    inFlight.current.add(line.itemId);
    setPending(new Set(inFlight.current));
    setRowError(null);
    try {
      // Each row is changed by its own ID, so other rows (even for the same group) are untouched
      if (quantity === 0) await api.delete(`/api/cart/items/${line.itemId}`);
      else await api.patch(`/api/cart/items/${line.itemId}`, { quantity });
      await load();
      window.dispatchEvent(new Event('cart-changed'));
    } catch (err) {
      // Shown beside this row only; the rest of the cart and its controls stay usable
      setRowError({ itemId: line.itemId, text: err.response?.data?.message || 'Could not update your cart.' });
    } finally {
      inFlight.current.delete(line.itemId);
      setPending(new Set(inFlight.current));
    }
  };

  // Cash: the server holds the portions until the deadline and empties the cart; no card is charged
  const placeCashOrder = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/cart/checkout', { paymentMethod: 'cash' });
      setCashOrder({ deadline: res.data.deadline });
      setMessage('');
      window.dispatchEvent(new Event('cart-changed'));
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not start checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  const checkout = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/cart/checkout');
      setPayment({ clientSecret: res.data.clientSecret, total: res.data.total });
      // The new checkout locks the cart on the server; mirror it so the edit controls disable at once
      setActive({ checkoutId: res.data.checkoutId, total: res.data.total, clientSecret: res.data.clientSecret });
      setMessage('');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not start checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  // The unpaid checkout (if any) is re-read from the server; its client secret is held in memory only
  const loadActive = async () => {
    try {
      const res = await api.get('/api/cart/checkout/active');
      setActive(res.data.checkout);
    } catch (err) {
      // No unpaid checkout to offer (or it is being processed): the normal Checkout button stays
      setActive(null);
    }
  };
  useEffect(() => { loadActive(); }, []);

  // Re-reads the server's checkout and cart state (used when a payment status is unresolved)
  const refreshCheckout = () => Promise.all([loadActive(), load()]);

  const resume = () => {
    setBusyCheckout('resume');
    setPayment({ clientSecret: active.clientSecret, total: active.total });
    setMessage('');
    setBusyCheckout(null);
  };

  const cancelCheckout = async () => {
    setBusyCheckout('cancel');
    try {
      await api.post('/api/cart/checkout/cancel');
      setPayment(null);
      setMessage('');
      await Promise.all([loadActive(), load()]);
      window.dispatchEvent(new Event('cart-changed'));
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not cancel your checkout.');
    } finally {
      setBusyCheckout(null);
    }
  };

  if (cashOrder) {
    return (
      <div className="max-w-3xl mx-auto bg-white shadow p-6 rounded-md">
        <h1 className="text-2xl font-semibold mb-2">{t('cash.awaiting')}</h1>
        <p className="text-gray-600 mb-4">{t('cash.placed', { deadline: formatDeadline(cashOrder.deadline) })}</p>
        <Link to="/my-orders" className="text-blue-600 hover:underline">{t('orders.title')}</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-3xl mx-auto bg-white shadow p-6 rounded-md">
        <h1 className="text-2xl font-semibold mb-2">{t('cart.paid')}</h1>
        <p className="text-gray-600 mb-3">{t('cart.paidHint')}</p>
        <Link to="/my-orders" className="text-blue-600 hover:underline">{t('orders.title')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white shadow p-6 rounded-md">
      <div className="bg-gray-800 -mx-6 -mt-6 mb-6 px-6 py-4 rounded-t-md">
        <img src="/logo-header.png" alt="Qercha" className="h-12 w-auto" />
      </div>
      {(quote ? quote.lines.length > 0 : message !== EMPTY_CART) && <h1 className="text-2xl font-semibold text-gray-800 mb-4">{t('cart.reviewOrder')}</h1>}
      {message && message !== EMPTY_CART && <p className="text-red-600 mb-3">{translateKnown(t, message)}</p>}
      {loading && !quote && !message && <p className="text-gray-500" role="status">{t('cart.loading', { defaultValue: 'Loading your cart...' })}</p>}
      {active && <p className="text-amber-700 mb-3">{t('cart.editBlocked')}</p>}

      {/* Shown from the server's active checkout, independent of the quote, so a quote error cannot hide it */}
      {active && !payment && (
        <div className="border border-amber-300 bg-amber-50 rounded p-3 mb-4 flex flex-wrap gap-2 items-center justify-between">
          <p className="text-gray-800">{t('cart.unpaidCheckout', { amount: money(active.total) })}</p>
          <div className="flex gap-2">
            <button type="button" onClick={resume} disabled={!!busyCheckout} className="bg-green-600 text-white px-4 py-2 rounded transition active:scale-[0.98] active:brightness-90 disabled:opacity-50">{busyCheckout === 'resume' ? t('cart.resuming', { defaultValue: 'Resuming...' }) : t('cart.resume')}</button>
            <button type="button" onClick={cancelCheckout} disabled={!!busyCheckout} className="border border-gray-400 text-gray-700 px-4 py-2 rounded transition active:scale-[0.98] active:brightness-90 disabled:opacity-50">{busyCheckout === 'cancel' ? t('cart.cancelling', { defaultValue: 'Cancelling...' }) : t('cart.cancelCheckout')}</button>
          </div>
        </div>
      )}

      {/* Fallback when the quote is unavailable: the same payment form, shown here so Resume still works */}
      {payment && !(quote && quote.lines.length > 0) && (
        <div className="border border-gray-200 rounded-md p-4 mb-4 space-y-3">
          <Elements stripe={getStripe()}>
            <PayForm clientSecret={payment.clientSecret} onPaid={paid} onRecheck={refreshCheckout} onCancel={cancelCheckout} cancelling={busyCheckout === 'cancel'} />
          </Elements>
          <button type="button" onClick={() => setPayment(null)} className="text-gray-600 hover:underline">{t('cart.close')}</button>
        </div>
      )}

      {quote && quote.lines.length > 0 && (
        <>
          <div>
          <div className="min-w-0">
          <h2 className="text-sm font-semibold uppercase text-gray-500 mb-2">{t('cart.items')}</h2>
          <ul className="divide-y border border-gray-200 rounded-md mb-4">
            {quote.lines.map((l) => (
              <li key={l.itemId} className="py-3 px-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="min-w-0 flex-1 basis-40 break-words">
                  <div className="font-semibold text-gray-800">{l.groupName}</div>
                  {rowError && rowError.itemId === l.itemId && <div className="text-sm text-amber-700">{translateKnown(t, rowError.text)}</div>}
                  {(quote.shortGroups || []).includes(l.groupId) && (
                    <div className="text-sm text-amber-700">{t('cart.notEnough')}</div>
                  )}
                  <div className="text-sm text-gray-600 break-words">{l.supplierDisplayName || l.supplierName} ·{t('portions.' + String(l.portion || '').toLowerCase())} {t('cart.portionWord')} · {money(l.amount)} · {l.fulfillment === 'delivery' ? `${t('cart.deliveryTo')} ${[l.deliveryAddress?.street, l.deliveryAddress?.city, stateLabel(l.deliveryAddress?.state), l.deliveryAddress?.zip].filter(Boolean).join(', ')}` : t('cart.pickup')}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" disabled={!!active || pending.has(l.itemId)} onClick={() => change(l, l.quantity - 1)} className="px-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50" aria-label={t('cart.decrease')}>−</button>
                  <span className="text-gray-800 font-medium min-w-[1.5rem] text-center">{l.quantity}</span>
                  <button type="button" disabled={!!active || pending.has(l.itemId)} onClick={() => change(l, l.quantity + 1)} className="px-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50" aria-label={t('cart.increase')}>+</button>
                  <button type="button" disabled={!!active || pending.has(l.itemId)} onClick={() => change(l, 0)} className="text-red-600 hover:underline ml-2 disabled:opacity-50">{t('cart.remove')}</button>
                </div>
              </li>
            ))}
          </ul>

          </div>
          <div>
          <div className="border border-gray-200 bg-gray-50 rounded-md p-4 mb-6 space-y-2 text-sm">
            <h2 className="font-semibold text-gray-800">{t('groups.summary')}</h2>
            <p>{t('cart.portionsTotal')} {money(quote.portionTotal)}</p>
            <p>{t('cart.feeTotal')} {money(quote.feeTotal)}</p>
            <p>{t('cart.deliveryTotal')} {money(quote.deliveryTotal)}</p>
            <p className="text-lg font-semibold">{t('cart.total')} {money(quote.total)}</p>
          </div>

          <div className="sticky bottom-0 bg-white pt-2 pb-1 md:static">
          {payment ? (
            <Elements stripe={getStripe()}>
              <PayForm clientSecret={payment.clientSecret} onPaid={paid} onRecheck={refreshCheckout} onCancel={cancelCheckout} cancelling={busyCheckout === 'cancel'} />
            </Elements>
          ) : !active && (
            <>
              {quote.cashAvailable && (
                <fieldset className="mb-3 space-y-1">
                  <legend className="font-semibold text-gray-800 mb-1">{t('cash.paymentMethod')}</legend>
                  <label className="flex items-center gap-2 text-gray-800">
                    <input type="radio" name="paymentMethod" value="card" checked={method === 'card'} onChange={() => setMethod('card')} />
                    {t('cash.byCard')}
                  </label>
                  <label className="flex items-center gap-2 text-gray-800">
                    <input type="radio" name="paymentMethod" value="cash" checked={method === 'cash'} onChange={() => setMethod('cash')} />
                    {t('cash.byCash')}
                  </label>
                </fieldset>
              )}
              <button
                type="button"
                onClick={method === 'cash' ? placeCashOrder : checkout}
                disabled={(quote.shortGroups || []).length > 0 || !verified || submitting}
                className="bg-green-600 text-white px-4 py-2 rounded w-full transition active:scale-[0.98] active:brightness-90 disabled:opacity-50"
              >
                {submitting ? t('cart.processing') : method === 'cash' ? t('cash.placeOrder') : t('cart.checkout')}
              </button>
            </>
          )}
          </div>
          </div>
          </div>
        </>
      )}

      {(quote ? quote.lines.length === 0 : message === EMPTY_CART) && (
        <div className="text-center py-8">
          <h2 className="text-lg font-semibold text-gray-800">{t('cart.empty')}</h2>
          <p className="text-gray-600 mt-1">{t('cart.emptyHint')}</p>
        </div>
      )}
    </div>
  );
}
